import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface DuesySource {
  id: string;
  title: string;
  timestamp: string | null;
  seconds: number | null;
  url: string | null;
}

export interface DuesyServerMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  sources: DuesySource[];
  unanswered: boolean;
  createdAt: string;
}

export interface DuesyDone {
  messageId: string | null;
  conversationId: string | null;
  content: string;
  sources: DuesySource[];
  unanswered: boolean;
  createdAt: string;
}

export type DuesyChatEvent =
  | { type: 'delta'; text: string }
  | { type: 'done'; data: DuesyDone }
  | { type: 'error'; message: string; retryable: boolean };

@Injectable({ providedIn: 'root' })
export class DuesyApiService {
  private readonly baseUrl = `${environment.dbApi.baseUrl}/duesy`;

  constructor(private http: HttpClient) {}

  status(): Observable<{ enabled: boolean; maxMessageLength?: number }> {
    return this.http.get<{ enabled: boolean; maxMessageLength?: number }>(`${this.baseUrl}/status`);
  }

  currentConversation(): Observable<{ conversation: { id: string; title: string } | null; messages: DuesyServerMessage[] }> {
    return this.http.get<any>(`${this.baseUrl}/conversations/current`);
  }

  deleteConversation(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/conversations/${encodeURIComponent(id)}`);
  }

  feedback(messageId: string, rating: 'up' | 'down', comment?: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/feedback`, { messageId, rating, comment });
  }

  /**
   * POST /chat streamed as server-sent events. HttpClient can't stream a POST
   * body, so this uses fetch and attaches the same Bearer token the
   * AddHeaderInterceptor uses. Unsubscribing aborts the request.
   */
  chat(body: { message: string; pageKey: string; conversationId?: string | null }): Observable<DuesyChatEvent> {
    return new Observable<DuesyChatEvent>((subscriber) => {
      const controller = new AbortController();
      const token = localStorage.getItem('dt-token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json', Accept: 'text/event-stream' };
      if (token) { headers['Authorization'] = `Bearer ${token}`; }

      (async () => {
        const response = await fetch(`${this.baseUrl}/chat`, {
          method: 'POST',
          headers,
          body: JSON.stringify(body),
          signal: controller.signal,
        });
        if (!response.ok || !response.body) {
          let message = 'Duesy could not answer just now. Please try again.';
          try {
            const json = await response.json();
            if (json && typeof json.message === 'string') { message = json.message; }
          } catch {
            // keep the default message
          }
          if (response.status === 401) { message = 'Your session has expired. Please sign in again.'; }
          subscriber.next({ type: 'error', message, retryable: response.status === 429 || response.status >= 500 });
          subscriber.complete();
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let finished = false;
        while (!finished) {
          const { value, done } = await reader.read();
          if (done) { break; }
          buffer += decoder.decode(value, { stream: true });
          let boundary: number;
          while ((boundary = buffer.indexOf('\n\n')) !== -1) {
            const block = buffer.slice(0, boundary);
            buffer = buffer.slice(boundary + 2);
            const event = parseSseBlock(block);
            if (!event) { continue; }
            subscriber.next(event);
            if (event.type !== 'delta') { finished = true; }
          }
        }
        if (!finished) {
          subscriber.next({ type: 'error', message: 'The connection to Duesy was interrupted. Please try again.', retryable: true });
        }
        subscriber.complete();
      })().catch((err) => {
        if (controller.signal.aborted) { return; }
        subscriber.next({ type: 'error', message: 'Duesy could not be reached. Check your connection and try again.', retryable: true });
        subscriber.complete();
      });

      return () => controller.abort();
    });
  }
}

export function parseSseBlock(block: string): DuesyChatEvent | null {
  let event = 'message';
  const dataLines: string[] = [];
  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) {
      event = line.slice(6).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trim());
    }
  }
  if (!dataLines.length) { return null; }
  let data: any;
  try {
    data = JSON.parse(dataLines.join('\n'));
  } catch {
    return null;
  }
  if (event === 'delta') { return { type: 'delta', text: String(data.text || '') }; }
  if (event === 'done') { return { type: 'done', data }; }
  if (event === 'error') { return { type: 'error', message: data.message, retryable: data.retryable !== false }; }
  return null;
}
